import React, { forwardRef, PropsWithChildren, useMemo } from 'react'
import cn from 'clsx'
import styled, { css } from 'styled-components'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { rem } from 'polished'

export const GROUP_GAPS = [0, 2, 4, 8, 12, 16, 20, 24, 32, 40, 48, 56, 72, 80] as const
export const GROUP_ALIGNS = ['start', 'center', 'stretch', 'end', 'space-between'] as const

const FlexJustifyCSS = css`
  ${GROUP_ALIGNS.map(align => `
    &-${align} {
      justify-content: ${align};
    }
  `)}
`
const FlexAlignCSS = css`
  ${GROUP_ALIGNS.map(align => `
    &-${align} {
      align-items: ${align};
    }
  `)}
`

const GroupDirectionsCSS = css`
  &-horizontal {
    flex-direction: row;

    &-reverse {
      flex-direction: row-reverse;
    }
  }

  &-vertical {
    flex-direction: column;

    &-reverse {
      flex-direction: column-reverse;
    }
  }

  &-horizontal, &-horizontal-reverse {
    &.align-h {
      ${FlexJustifyCSS}
    }

    &.align-v {
      ${FlexAlignCSS}
    }
  }

  &-vertical, &-vertical-reverse {
    &.align-h {
      ${FlexAlignCSS}
    }

    &.align-v {
      ${FlexJustifyCSS}
    }
  }

  ${mediaQueryUp.sm} {
    &-horizontal, &-horizontal-reverse {
      &.align-h-sm {
        ${FlexJustifyCSS}
      }

      &.align-v-sm {
        ${FlexAlignCSS}
      }
    }

    &-vertical, &-vertical-reverse {
      &.align-h-sm {
        ${FlexAlignCSS}
      }

      &.align-v-sm {
        ${FlexJustifyCSS}
      }
    }
  }
`

const GROUP_V_GAP_CSS_VAR = '--group-v-gap'
const GROUP_H_GAP_CSS_VAR = '--group-h-gap'
const GROUP_V_GAP_SM_CSS_VAR = '--group-v-gap-sm'
const GROUP_H_GAP_SM_CSS_VAR = '--group-h-gap-sm'

const GroupContainer = styled.div`
  display: flex;
  row-gap: var(${GROUP_V_GAP_CSS_VAR});
  column-gap: var(${GROUP_H_GAP_CSS_VAR});

  ${mediaQueryUp.sm} {
    row-gap: var(${GROUP_V_GAP_SM_CSS_VAR});
    column-gap: var(${GROUP_H_GAP_SM_CSS_VAR});
  }

  &.dir {
    ${GroupDirectionsCSS}

    &-sm {
      ${mediaQueryUp.sm} {
        ${GroupDirectionsCSS}
      }
    }
  }

  &.wrap {
    &-nowrap {
      flex-wrap: nowrap;
    }
    &-wrap {
      flex-wrap: wrap;
    }
    &-wrap-reverse {
      flex-wrap: wrap-reverse;
    }
  }

  &.full-width {
    width: 100%;
  }

  &.full-height {
    height: 100%;
  }
`

type GroupGap = typeof GROUP_GAPS[number] | `${typeof GROUP_GAPS[number]} ${typeof GROUP_GAPS[number]}`

function parseGroupGaps(gap: GroupGap | undefined): [
  parsedVGap: number | undefined,
  parsedHGap: number | undefined,
] {
  if (gap === undefined) {
    return [undefined, undefined]
  }
  if (typeof gap === 'string') {
    const [extractedVGap, extractedHGap] = gap.split(' ')

    return [
      Number(extractedVGap),
      Number(extractedHGap),
    ]
  }

  return [
    gap,
    gap,
  ]
}

interface Props {
  className?: string
  direction: 'horizontal' | 'vertical' | 'horizontal-reverse' | 'vertical-reverse'
  /**
   * apply spacing to children
   * @example
   * as a number `gap={8}` sets both vertical and horizontal spacing to 8
   * as a string ("vertical horizontal") `gap="8 16"` sets vertical spacing to 8 and horizontal spacing to 16
   * */
  gap?: GroupGap
  horizontalAlign?: typeof GROUP_ALIGNS[number]
  smDirection?: 'horizontal' | 'vertical' | 'horizontal-reverse' | 'vertical-reverse'
  /** similar to `gap`, applies spacing to children on breakpoint `sm` and up */
  smGap?: GroupGap
  smHorizontalAlign?: typeof GROUP_ALIGNS[number]
  smVerticalAlign?: typeof GROUP_ALIGNS[number]
  verticalAlign?: typeof GROUP_ALIGNS[number]
  wrap?: 'nowrap' | 'wrap' | 'wrap-reverse'
  fullWidth?: boolean
  fullHeight?: boolean
  as?: React.ComponentProps<typeof GroupContainer>['as'];
}

const Group = forwardRef<HTMLDivElement, PropsWithChildren<Props>>((props, ref) => {
  const {
    children,
    className,
    direction,
    smDirection,
    gap,
    smGap,
    horizontalAlign,
    smHorizontalAlign,
    verticalAlign,
    smVerticalAlign,
    wrap,
    fullWidth,
    fullHeight,
    as,
  } = props

  const { parsedVGap, parsedHGap, parsedSmVGap, parsedSmHGap } = useMemo(() => {
    const [parsedVGap, parsedHGap] = parseGroupGaps(gap)
    const [parsedSmVGap, parsedSmHGap] = parseGroupGaps(smGap)

    return {
      parsedVGap: rem(parsedVGap ?? 0),
      parsedHGap: rem(parsedHGap ?? 0),
      parsedSmVGap: rem(parsedSmVGap ?? parsedVGap ?? 0),
      parsedSmHGap: rem(parsedSmHGap ?? parsedHGap ?? 0),
    }
  }, [gap, smGap])

  return <GroupContainer
    ref={ref}
    as={as}
    className={cn(
      className,
      `dir-${direction}`,
      smDirection ? `dir-sm-${smDirection}` : undefined,
      horizontalAlign ? `align-h-${horizontalAlign}` : undefined,
      smHorizontalAlign ? `align-h-sm-${smHorizontalAlign}` : undefined,
      verticalAlign ? `align-v-${verticalAlign}` : undefined,
      smVerticalAlign ? `align-v-sm-${smVerticalAlign}` : undefined,
      wrap ? `wrap-${wrap}` : undefined,
      {
        'full-width': fullWidth,
        'full-height': fullHeight,
      },
    )}
    style={{
      [GROUP_V_GAP_CSS_VAR]: parsedVGap,
      [GROUP_H_GAP_CSS_VAR]: parsedHGap,
      [GROUP_V_GAP_SM_CSS_VAR]: parsedSmVGap,
      [GROUP_H_GAP_SM_CSS_VAR]: parsedSmHGap,
    }}
  >
    {children}
  </GroupContainer>
})

export default Group
